/**
 * AssignReversibilityBlock — the Brief 3 PR3 reversibility controls
 * (Reassign / Unassign from Assigned · Move back to any earlier stage),
 * extracted from WorkItemDrawer (UX-11 PR-2).
 *
 * Soak 2026-07-25: the "Assign care manager" bar moved OUT of this block
 * into the checklist's checkpoint slot (StageChecklist advanceOverride) —
 * it now sits at the end of the Ready-for-Assignment section like every
 * other stage's advance bar (founder's ask). This block keeps the
 * reversibility row + the assign/move error line (the Assign dialog's
 * errors still land here via assignError).
 *
 * The MoveStageDialog (assign/reassign picker) and the styled confirms
 * stay container-level with the dialog cluster; this block requests them
 * via onStartAssign / requestConfirm. The gate predicates are computed by
 * the container — the block renders, it does not decide.
 */

import { type ReactNode } from 'react';
import type { PipelineStageWire, WorkItemDetail } from '@/lib/api';
import type { useMoveStage, useUnassignCm } from '@/lib/workspace';
import { TextAction } from '@/components/workspace/TextAction';
import { DrawerActionRow } from './drawerLayout';
import { SelectField } from '@/components/primitives/SelectField';

export interface ConfirmActionRequest {
  title: string;
  /** Optional: a confirm whose title says it all carries no body line. */
  body?: string;
  confirmLabel: string;
  run: () => void;
  /** 0155 — a destructive action (e.g. Delete intake) gets a pepper confirm button. */
  destructive?: boolean;
}

export interface AssignReversibilityBlockProps {
  detail: WorkItemDetail;
  canReassignCm: boolean;
  canUnassignCm: boolean;
  canMoveBack: boolean;
  priorStages: readonly PipelineStageWire[];
  stageLabelByKey: Map<string, string>;
  assignError: string | null;
  onStartAssign: () => void;
  requestConfirm: (req: ConfirmActionRequest) => void;
  /** Composed in the container: maps a gate/move error to assignError copy. */
  onBlockedError: (err: unknown) => void;
  unassignCm: ReturnType<typeof useUnassignCm>;
  moveStage: ReturnType<typeof useMoveStage>;
}

export function AssignReversibilityBlock({
  detail,
  canReassignCm,
  canUnassignCm,
  canMoveBack,
  priorStages,
  stageLabelByKey,
  assignError,
  onStartAssign,
  requestConfirm,
  onBlockedError,
  unassignCm,
  moveStage,
}: AssignReversibilityBlockProps): ReactNode {
  return (
    <>
      {/* Brief 3 PR3 — reversibility: Reassign / Unassign (from Assigned) ·
          Move back to any earlier stage (the other worked stages). */}
      {(canReassignCm || canMoveBack) && (
        <DrawerActionRow label="Change" testId="drawer-reversibility">
          {canReassignCm && (
            <TextAction data-testid="drawer-reassign-cm" onClick={onStartAssign}>
              Reassign CM
            </TextAction>
          )}
          {canUnassignCm && (
            <TextAction
              data-testid="drawer-unassign-cm"
              onClick={() => requestConfirm({
                title: 'Unassign care manager?',
                body: 'Take the care manager off this case and return it to Ready for Assignment? Care units stay enrolled.',
                confirmLabel: 'Unassign',
                run: () => unassignCm.mutate(detail.id, { onError: onBlockedError }),
              })}
            >
              Unassign CM
            </TextAction>
          )}
          {canMoveBack && (
            <label className="ml-1 inline-flex items-center gap-1.5 text-xs text-sage-400">
              Move back to
              <SelectField size="compact"
                data-testid="drawer-move-back"
                aria-label="Move back to an earlier stage"
                value=""
                onChange={(e) => {
                  const key = e.target.value;
                  if (!key) return;
                  requestConfirm({
                    title: 'Move back a stage?',
                    body: `Move this case back to ${stageLabelByKey.get(key) ?? key}? Any conversion and care unit stay. Only the stage changes.`,
                    confirmLabel: 'Move back',
                    run: () => moveStage.mutate(
                      { id: detail.id, input: { to_stage: key, auto_complete_stage: false } },
                      { onError: onBlockedError },
                    ),
                  });
                }}
              >
                <option value="">…</option>
                {priorStages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </SelectField>
            </label>
          )}
        </DrawerActionRow>
      )}

      {/* The Assign dialog's / blocked-move errors still surface here (the
          assign bar itself lives in the checklist's checkpoint slot now).
          pepper-deep, not pepper: index.css documents pepper-deep as the
          small-text danger tone (6.29:1 on paper) — plain pepper at 12px
          misses AA (drawer AA sweep, soak r2). */}
      {assignError && <p role="alert" data-testid="drawer-assign-error" className="text-xs text-pepper-deep mt-1 border-t-0">{assignError}</p>}
    </>
  );
}
